import { ContextIdService } from '../ContextIdService/ContextIdService';
import { Client } from '@stomp/stompjs';
import { IMessage } from '@stomp/stompjs';
import { IFrame } from '@stomp/stompjs';
import { StompSubscription } from '@stomp/stompjs';
import { ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs';
import { filter } from 'rxjs';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs';
import { timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RouterModule } from '@angular/router';
import { ParamMap } from '@angular/router';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
@Injectable({
  providedIn: 'root'
})
export class EventBusService
{
	private stompClient?: Client;
  private readonly eventBusUrl: string = '/eventbus'; // Update as needed
  private reconnectAttempts: number = 0;
  private readonly reconnectDelay: number = 5000;
  private readonly maxReconnectAttempts: number = 99999;
  private readonly maxReconnectDelay: number = 30000;

  private registeredListeners = new Set<string>();
  private totalExpectedListeners = 0;
  private listenerReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private connectionState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private pendingListeners: string[] = [];
  private stompSubscriptions: Map<string, StompSubscription> = new Map();

  private destroy$ = new Subject<void>();
  private messageSubjects: Map<string, Map<string, Subject<any>>> = new Map();
  private messageQueue: Array<{ action: string; data: object; eventType: string; event?: any; component?: ElementRef<any> }> = [];
  private guid: string
  private currentContextId?: string | null;
	constructor( private routeLocation: Location, private router: Router, private route: ActivatedRoute, private contextIdService : ContextIdService )
	{
	this.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isConnected) => {
          if (isConnected) {
              console.log('[EventBusService] Connection restored. Re-registering all listeners...');
              // Requeue all existing listeners
              this.messageSubjects.forEach((_, address) => {
                  this.queueListener(address);
              });
              this.processQueuedMessages();
          } else {
              console.warn('[EventBusService] Connection lost. Waiting for reconnection...');
              this.stompSubscriptions.clear();
          }
      });
	this.guid = this.generateGUID();
    this.contextIdService.setContextId(this.guid)
	this.initializeEventBus();
	// Track contextId changes and ensure private subscriptions are bound correctly
this.currentContextId = this.contextIdService.contextId();
this.contextIdService.getContextIdObservable()
    .pipe(takeUntil(this.destroy$))
    .subscribe((newId) => {
        const oldId = this.currentContextId;
        this.currentContextId = newId;

        // For all existing listeners, ensure subscriptions include new contextId
        this.messageSubjects.forEach((_, address) => {
            this.ensurePrivateSubscriptions(address);

            // Clean up old contextId subscriptions if they exist and are not the GUID
            if (oldId && oldId !== this.guid) {
                const oldPrivateAddress = `${oldId}.${address}`;
                const oldKey = this.privateSubscriptionKey(oldPrivateAddress);
                const oldSub = this.stompSubscriptions.get(oldKey);
                if (oldSub) {
                    oldSub.unsubscribe();
                    this.stompSubscriptions.delete(oldKey);
                }
            }
        });
    });
	this.listen(this.guid,this.guid).subscribe(data => {
            this.processResult(data);
        });
    const everyoneId = this.generateGUID();
    this.listen("Everyone",everyoneId).subscribe(data => {
        this.processResult(data);
    });
    const sessionStorage = this.generateGUID();
    this.listen("SessionStorage",sessionStorage).subscribe(data => {
        Object.keys(data).forEach(prop => {
                window.sessionStorage.setItem(prop, data[prop]);
                if (prop === 'contextId') {
                    this.contextIdService.setContextId(data[prop]);
                }
            });
    });
    const localStorage = this.generateGUID();
    this.listen("LocalStorage",localStorage).subscribe(data => {
        Object.keys(data).forEach(prop => {
                window.localStorage.setItem(prop, data[prop]);
            });
    });
	}
	
    /**
     * Utility to unregister all listeners. Useful for cleanup operations.
     */
    unregisterAllListeners(): void {
        if (this.stompClient && this.messageSubjects.size > 0) {
            this.messageSubjects.forEach((handlers, address) => {
                // Unregister each handler for the address
                handlers.forEach((_, handlerId) => {
                    this.unregisterListener(address, handlerId);
                });
            });
            console.log("[EventBusService] Unregistered all listeners.");
        } else {
            console.warn("[EventBusService] No active listeners to unregister.");
        }
    }


  /**
   * Method to generate a GUID.
   */
  private generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

	
    processResult(response: any) {
            if(!response)
            {
                console.log('No response object received!?');
                //debugger;
                return;
            }

            if (response.localStorage) {
                Object.keys(response.localStorage).forEach(prop => {
                    window.localStorage.setItem(prop, response.localStorage[prop]);
                });
            }
            if (response.sessionStorage) {
                Object.keys(response.sessionStorage).forEach(prop => {
                    window.sessionStorage.setItem(prop, response.sessionStorage[prop]);
                    if (prop === 'contextId') {
                        this.contextIdService.setContextId(response.sessionStorage[prop]);
                    }
                });
            }
            if (response.features) {
            }
            if (response.reactions) {
                for (let reaction of response.reactions) {
                    const react: any = reaction;
                    if ("RedirectUrl" == react.reactionType) {
                        this.router.navigateByUrl(react.reactionMessage);
                    }
                }
            }
        }


	
    /**
       * Remove a specific handler for an EventBus address by its ID.
       * If no listeners remain for the address, the handler is fully removed from the EventBus.
       * @param address The address of the listeners to check.
       * @param handlerId The specific handler ID to unregister.
       */
      unregisterListener(address: string, handlerId: string): void {
          const normalizedAddress = this.normalizeAddress(address);

          if (this.messageSubjects.has(normalizedAddress)) {
              const listeners = this.messageSubjects.get(normalizedAddress);

              if (listeners!.has(handlerId)) {
                  // Remove this specific handler
                  listeners!.get(handlerId)!.complete(); // Notify subscribers of closure
                  listeners!.delete(handlerId);
                  console.log(`[EventBusService] Unregistered handler ID "${handlerId}" for address: "${normalizedAddress}"`);

                  // If no listeners remain for this address, clean up
                  if (listeners!.size === 0) {
                      this.messageSubjects.delete(normalizedAddress);
                      this.registeredListeners.delete(normalizedAddress);
                      const addressKey = this.addressSubscriptionKey(normalizedAddress);
                      const subscription = this.stompSubscriptions.get(addressKey);
                      if (subscription) {
                          subscription.unsubscribe();
                          this.stompSubscriptions.delete(addressKey);
                      }

                      const ids: Array<string> = [];
                      if (this.guid) {
                          ids.push(this.guid);
                      }
                      const ctxId = this.currentContextId ?? this.contextIdService.contextId();
                      if (ctxId && !ids.includes(ctxId)) {
                          ids.push(ctxId);
                      }

                      ids.forEach((id) => {
                          const privateKey = this.privateSubscriptionKey(`${id}.${normalizedAddress}`);
                          const privateSub = this.stompSubscriptions.get(privateKey);
                          if (privateSub) {
                              privateSub.unsubscribe();
                              this.stompSubscriptions.delete(privateKey);
                          }
                      });
                      console.log(`[EventBusService] All handlers removed for address: "${normalizedAddress}"`);
                  }
              } else {
                  console.warn(`[EventBusService] No handler found with ID "${handlerId}" for address: "${normalizedAddress}"`);
              }
          } else {
              console.warn(`[EventBusService] No listeners found for address: "${normalizedAddress}"`);
          }
      }
	
    getParametersObject() : object {
        try {
            var search = location.search.substring(1);
            return JSON.parse('{"' + decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');
        } catch (err) {
            return {};
        }
    }


    /**
     * Ensure that private subscriptions exist for both the generated GUID and the current ContextId.
     */
    private ensurePrivateSubscriptions(address: string): void {
        const ids: Array<string> = [];
        if (this.guid) {
            ids.push(this.guid);
        }
        const ctx = this.currentContextId ?? this.contextIdService.contextId();
        if (ctx && !ids.includes(ctx)) {
            ids.push(ctx);
        }

        if (!this.stompClient?.connected) {
            return;
        }

        ids.forEach((id) => {
            const privateAddress = `${id}.${address}`;
            const subKey = this.privateSubscriptionKey(privateAddress);
            if (!this.stompSubscriptions.has(subKey)) {
                const privateSubscription = this.stompClient!.subscribe(`/toStomp/${privateAddress}`, (message) => {
                    try {
                        const body = JSON.parse(message.body);
                        this.emitToHandlers(address, body);
                    } catch (error) {
                        console.error(`[EventBusService] Error parsing message on private address "${privateAddress}":`, error);
                    }
                });
                this.stompSubscriptions.set(subKey, privateSubscription);
                // console.log(`[EventBusService] Private Listener registered for address: "${privateAddress}"`);
            }
        });
    }

	private queueListener(address: string): void {
          // Check if the listener is already queued
          const isAlreadyQueued = this.pendingListeners.includes(address);

          if (!isAlreadyQueued) {
              //console.log(`[EventBusService] Queuing listener for address: "${address}"`);
              this.pendingListeners.push(address);
          }

          // Process all queued listeners when the connection becomes ready
          this.connectionState$
              .pipe(
                  takeUntil(this.destroy$),
                  filter(isConnected => isConnected), // Wait for a true (connected) state
                  take(1) // Process the queue on the first successful connection
              )
              .subscribe(() => this.processPendingListeners());
      }

	private processPendingListeners(): void {
          if (!this.stompClient?.connected) {
              console.warn('[EventBusService] STOMP client not connected yet!');
              return;
          }

          //console.log('[EventBusService] Processing queued listeners...');
          while (this.pendingListeners.length > 0) {
              const address = this.pendingListeners.shift()!;
              const addressKey = this.addressSubscriptionKey(address);

              if (!this.stompSubscriptions.has(addressKey)) {
                  // Subscribe once per address and fan out to all local handlers
                  const subscription = this.stompClient.subscribe(`/toStomp/${address}`, (message) => {
                      try {
                          // Parse the message body
                          const body = JSON.parse(message.body);
                          this.emitToHandlers(address, body);
                      } catch (error) {
                          console.error(`[EventBusService] Error parsing message on address "${address}":`, error);
                      }
                  });

                  // Store the subscription for later unsubscribe
                  this.stompSubscriptions.set(addressKey, subscription);
                  console.log(`[EventBusService] Listener registered for address: "${address}"`);
              }

              // Ensure private subscriptions for both GUID and current ContextId
              this.ensurePrivateSubscriptions(address);
          }
      }

  /**
   * Initializes the EventBus connection.
   */
  private initializeEventBus(): void {
    //this.connect(); // Connect to the EventBus
    this.connectWithTimeout(); // Connect to the EventBus

    // Set up a listener to monitor the connection state
   /* if (this.eventBus) {
        this.eventBus.onopen = () => {
            console.log('[EventBusService] Connection to EventBus established.');
            this.connectionState$.next(true); // Connection is open
            this.reconnectAttempts = 0;
        };

        this.eventBus.onclose = () => {
            console.warn('[EventBusService] Connection to EventBus lost.');
            this.connectionState$.next(false); // Connection is closed
            this.reconnect(); // Attempt to reconnect
        };
    }*/

    // Handle connection state updates
    this.connectionState$.pipe(takeUntil(this.destroy$)).subscribe((isConnected) => {
        if (!isConnected) {
            console.warn('[EventBusService] EventBus is disconnected. Trying to reconnect...');
        }
    });
}

  /**
   * Connect to the Vert.x EventBus using STOMP.
   */
  private connect(): void {
    this.stompClient = new Client({
      brokerURL: `${window.location.protocol === 'https:' ? 'wss://' : 'ws://'}${window.location.host}${this.eventBusUrl}`,
      reconnectDelay: this.reconnectDelay,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        console.log('[EventBus] Connected via STOMP.');
        this.reconnectAttempts = 0;
        this.connectionState$.next(true); // Notify successful connection
      },
      onDisconnect: () => {
        console.warn('[EventBus] Disconnected.');
        this.connectionState$.next(false); // Notify disconnection
      },
      onStompError: (frame) => {
        console.error('[EventBus] Error:', frame.headers['message']);
        this.connectionState$.next(false); // Notify disconnection on error
      }
    });

    this.stompClient.activate();
  }

/**
     * Connects to the EventBus with a timeout using STOMP.
     * If the connection is not established within the defined timeout, it triggers a reconnect attempt.
     */
    private connectWithTimeout(): void {
      const connectionTimeout = 10000; // 10 seconds

      let connectionEstablished = false;

      const timeout = setTimeout(() => {
        if (!connectionEstablished) {
          console.error('[EventBusService] Connection timed out. Retrying...');
          this.stompClient?.deactivate(); // Ensure the STOMP client is deactivated
          this.scheduleReconnect(); // Trigger a reconnect attempt
        }
      }, connectionTimeout);

      // Create STOMP client instance
      this.stompClient = new Client({
        brokerURL: `${window.location.protocol === 'https:' ? 'wss://' : 'ws://'}${window.location.host}${this.eventBusUrl}`,
        reconnectDelay: this.reconnectDelay,
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 10000,
        onConnect: () => {
          console.log('[EventBusService] Connection to STOMP established.');
          connectionEstablished = true; // Mark connection as established
          clearTimeout(timeout); // Clear the timeout
          this.connectionState$.next(true); // Update connection state
          this.reconnectAttempts = 0; // Reset reconnect attempts
        },
        onDisconnect: () => {
          console.warn('[EventBusService] Connection lost. Retrying...');
          this.connectionState$.next(false);
          if (!connectionEstablished) {
            clearTimeout(timeout); // Ensure timeout is cleared
          }
          this.scheduleReconnect(); // Trigger a reconnect attempt
        },
        onStompError: (frame) => {
          console.error('[EventBusService] STOMP Error:', frame.headers['message']);
          this.connectionState$.next(false);
        }
      });

      this.stompClient.activate();
    }

    private scheduleReconnect(): void {
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            const delay = Math.min(
              this.reconnectDelay * Math.pow(2, this.reconnectAttempts), // Exponential backoff
              this.maxReconnectDelay
            );

            setTimeout(() => {
              console.log(`[EventBusService] Reconnecting... Attempt ${this.reconnectAttempts + 1}`);
              this.reconnectAttempts++;
              this.connectWithTimeout(); // Retry connection
            }, delay);
          } else {
            console.error('[EventBusService] Maximum reconnect attempts reached. Connection failed.');
          }
        }

  /**
   * Reconnect to the EventBus with a backoff strategy using STOMP.
   */
  private reconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(
        `[EventBus] Maximum reconnect attempts (${this.maxReconnectAttempts}) reached.`
      );
      return;
    }

    this.reconnectAttempts++;
    console.log(`[EventBus] Reconnecting via STOMP... (Attempt #${this.reconnectAttempts})`);

    // Ensure the old client is deactivated
    if (this.stompClient?.active) {
      this.stompClient.deactivate();
    }

    setTimeout(() => this.connect(), this.reconnectDelay);
  }

  /**
   * Wait for all listeners (directives) to be ready before processing sends.
   */
  public waitForListeners(totalListeners: number): void {
    this.totalExpectedListeners = totalListeners;
    this.checkAllListenersReady();
  }


  /**
     * Normalize an input address to ensure it's always a string.
     * @param address The address to normalize (can be string, array, or array-like string).
     * @returns A normalized address as a plain string.
     */
    private normalizeAddress(address: string | string[]): string {
        if (Array.isArray(address)) {
            // If it's an array, use the first element
            return address[0];
        }

        if (typeof address === 'string') {
            try {
                // Check if it's a stringified array (e.g., "['SessionLinesListWebUpdates']")
                const parsedAddress = JSON.parse(address);
                if (Array.isArray(parsedAddress)) {
                    // If it's a valid array, return the first element
                    return parsedAddress[0];
                }
            } catch (error) {
                // If parsing fails, assume it's already a valid string
            }
        }

        // Fallback: return address as-is
        return address;
    }

    private addressSubscriptionKey(address: string): string {
        return `addr:${address}`;
    }

    private privateSubscriptionKey(privateAddress: string): string {
        return `priv:${privateAddress}`;
    }

    private emitToHandlers(address: string, body: any): void {
        const handlers = this.messageSubjects.get(address);
        if (!handlers) {
            return;
        }
        handlers.forEach((subject) => {
            subject.next(body);
        });
    }


      /**
          * Subscribe to an EventBus address with a unique ID.
          * @param address The address to listen to.
          * @param handlerId A unique identifier for the handler (or generated automatically).
          * @returns Observable for messages on the address.
          */
         listen(address: string, handlerId: string): Observable<any> {
             // Ensure a Subject exists for the given address and handler ID
             if (!this.messageSubjects.has(address)) {
                 this.messageSubjects.set(address, new Map());
             }

             const handlers = this.messageSubjects.get(address)!;
             if (!handlers.has(handlerId)) {
                 handlers.set(handlerId, new Subject<any>());
             }

             const listenerSubject = handlers.get(handlerId)!;

             // Add the listener to the pending queue
             this.queueListener(address);

             return listenerSubject.asObservable();
         }


         /**
          * Generate a unique identifier for handlers.
          * @returns A random string ID.
          */
         private generateUniqueId(): string {
             return Math.random().toString(36).substr(2, 9); // Example: "k39uflv7a"
         }

  /**
   * Send a message to a specific address over the EventBus.
   * @param address Address to send the message to.
   * @param message Payload to send to the address.
   */
  send(action: string, data: object, eventType: string, event?: any, component?: ElementRef<any>): void {
          const message = { action, data, eventType, event, component };

          // Check connection state
          if (!this.connectionState$.value) {
              console.warn('[EventBus] Connection is not ready. Message queued.');
              this.messageQueue.push(message);
              return;
          }

          // Connection is ready, send the message
          this.sendMessageNow(message);
      }

      private sendMessageNow(message: { action: string; data: object; eventType: string; event?: any; component?: ElementRef<any> }) {
          const news: any = {};
          news.data = message.data;
          news.action = message.action;
          news.data.guid = this.guid;
          news.data.url = window.location;
          news.data.localStorage = window.localStorage;
          news.data.sessionStorage = window.sessionStorage;
          news.data.parameters = this.getParametersObject();
          news.data.hashbang = window.location.hash;
          news.data.route = this.routeLocation.path();
          news.data.state = this.routeLocation.getState();
          news.data.history = history.state;
          news.data.datetime = new Date().getUTCDate();
          news.data.eventType = message.eventType;
          news.data.attributes = {};

            if (message.component?.nativeElement?.attributes) {
                const attributes = message.component.nativeElement.attributes;

                // Convert attributes into a plain object
                for (let i = 0; i < attributes.length; i++) {
                    const attr = attributes[i];
                    news.data.attributes[attr.name] = attr.value;
                }
            }

          news.data.headers = {};
          news.data.headers.useragent = navigator.userAgent;
          news.data.headers.cookieEnabled = navigator.cookieEnabled;
          news.data.headers.appName = navigator.appName;
          news.data.headers.appVersion = navigator.appVersion;
          news.data.headers.language = navigator.language;

          if (message.event) {
              news.event = JSON.stringify(message.event);
          }

          if (this.stompClient?.connected) {
              this.stompClient.publish({
                  destination: '/toBus/incoming',
                  body: JSON.stringify(news),
                  headers: { 'content-type': 'application/json' }
              });
              //console.log(`[EventBus] Message sent via STOMP:`, news);
          } else {
              console.error('[EventBus] STOMP client is not connected.');
          }
      }

      private processQueuedMessages(): void {
          while (this.messageQueue.length > 0) {
              const message = this.messageQueue.shift(); // Remove the first message from the queue
              if (message) {
                  this.sendMessageNow(message);
              }
          }
      }

  /**
   * Register a listener and mark it as ready.
   */
  private registerListener(address: string): void {
    this.registeredListeners.add(address);
    this.checkAllListenersReady();
  }

  /**
   * Check if all necessary listeners (directives) are ready.
   */
  private checkAllListenersReady(): void {
    if (
      this.totalExpectedListeners > 0 &&
      this.registeredListeners.size >= this.totalExpectedListeners
    ) {
      this.listenerReady$.next(true); // Mark as ready
      console.log('[EventBus] All listeners registered.');
    }
  }

  /**
   * Wait until all listeners are marked as ready.
   */
  private waitForAllListeners(): Promise<void> {
    return new Promise(resolve => {
      this.listenerReady$.pipe(takeUntil(this.destroy$)).subscribe(isReady => {
        if (isReady) resolve();
      });
    });
  }

  /**
   * Disconnect the service and clean up resources.
   */
  public disconnect(): void {
    if (this.stompClient?.connected) {
      this.stompClient.deactivate();
    }
    console.log('[EventBus] Disconnected.');
    this.destroy$.next();
    this.destroy$.complete();
  }


}
